/**
 * Shared frame scheduler for LIVE chart passes.
 *
 * Each chart used to run its LIVE pass in its own requestAnimationFrame, so in a multi-chart layout every chart
 * updated (and lightweight-charts redrew every pane) in the same frame. Passes now share a per-frame time budget,
 * and when several charts are live, charts the user is not hovering refresh at most every BACKGROUND_INTERVAL_MS.
 * Ticks arriving in between are coalesced: the pass always renders the latest data.
 */
export interface LiveUpdateTarget {
    runLivePass(): void
    /** Priority charts (e.g. the hovered one) skip the background throttle and run first. */
    isLiveUpdatePriority(): boolean
}

const FRAME_BUDGET_MS = 6
const BACKGROUND_INTERVAL_MS = 200

const liveTargets = new Set<LiveUpdateTarget>()
const pending = new Set<LiveUpdateTarget>()
const lastRunAt = new WeakMap<LiveUpdateTarget, number>()
let rafId: number | null = null

function ensureFrame() {
    if (rafId == null) {
        rafId = requestAnimationFrame(flush)
    }
}

function flush() {
    rafId = null
    const start = performance.now()
    const throttleBackground = liveTargets.size > 1
    const ordered = Array.from(pending).sort(
        (a, b) => Number(b.isLiveUpdatePriority()) - Number(a.isLiveUpdatePriority())
    )
    let ranAny = false
    for (const target of ordered) {
        if (ranAny && performance.now() - start > FRAME_BUDGET_MS) break
        const priority = target.isLiveUpdatePriority()
        if (throttleBackground && !priority && start - (lastRunAt.get(target) ?? 0) < BACKGROUND_INTERVAL_MS) {
            continue
        }
        pending.delete(target)
        lastRunAt.set(target, start)
        ranAny = true
        target.runLivePass()
    }
    if (pending.size) {
        ensureFrame()
    }
}

export function scheduleLiveUpdate(target: LiveUpdateTarget) {
    liveTargets.add(target)
    pending.add(target)
    ensureFrame()
}

/** Drops a queued pass; the target stops counting as live until it schedules again. */
export function cancelLiveUpdate(target: LiveUpdateTarget) {
    pending.delete(target)
    liveTargets.delete(target)
}
